import { useState, useEffect } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { getStudentDetail } from './api/studentsApi'
import { getBiometricRecords } from './api/studentScreensApi'

export default function StudentBiometricScreen() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const studentId = searchParams.get('id')

  const [student, setStudent] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [fromDate, setFromDate] = useState('')
  const [toDate, setToDate] = useState('')
  const [records, setRecords] = useState([])

  useEffect(() => {
    loadStudent()
  }, [studentId])

  useEffect(() => {
    if (fromDate && toDate) loadRecords()
    else setRecords([])
  }, [studentId, fromDate, toDate])

  const loadStudent = async () => {
    try {
      const data = await getStudentDetail(studentId)
      setStudent(data)
    } catch {
      setStudent({ id: studentId, firstName: 'Student', lastName: '' })
    }
  }

  const loadRecords = async () => {
    setIsLoading(true)
    try {
      const rows = await getBiometricRecords({ fromDate, toDate, studentId })
      setRecords(rows)
    } catch {
      setRecords([])
    } finally {
      setIsLoading(false)
    }
  }

  const goBack = () => navigate('/admission/students/list')
  const studentName = student ? `${student.firstName || ''} ${student.lastName || ''}`.trim() : 'Student'
  const entryCount = records.length

  return (
    <div className="mx-auto flex w-full max-w-[1480px] flex-col gap-6 pb-8">
      <section className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 text-sm text-on-surface-variant">
            <span>Admission</span>
            <span className="material-symbols-outlined text-xs">chevron_right</span>
            <span>Students</span>
            <span className="material-symbols-outlined text-xs">chevron_right</span>
            <span className="font-semibold text-primary">Biometric Records</span>
          </div>
          <h1 className="mt-2 text-3xl font-bold text-on-surface">
            Biometric Records — {studentName}
          </h1>
        </div>
        <button
          onClick={goBack}
          className="inline-flex items-center gap-2 rounded-xl bg-secondary-container px-6 py-3 text-sm font-bold text-secondary hover:bg-secondary-container/80"
        >
          <span className="material-symbols-outlined text-base">arrow_back</span>
          Back to Students
        </button>
      </section>

      <div className="flex items-center gap-3 rounded-2xl border border-outline-variant/30 bg-surface-container-low p-4">
        <label className="grid gap-1.5">
          <span className="text-[10px] font-black uppercase tracking-[0.14em] text-on-surface-variant">From Date</span>
          <input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            className="rounded-xl border-0 bg-surface-container-lowest px-4 py-2.5 text-sm text-on-surface outline-none focus:ring-2 focus:ring-primary/20"
          />
        </label>
        <label className="grid gap-1.5">
          <span className="text-[10px] font-black uppercase tracking-[0.14em] text-on-surface-variant">Till Date</span>
          <input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            className="rounded-xl border-0 bg-surface-container-lowest px-4 py-2.5 text-sm text-on-surface outline-none focus:ring-2 focus:ring-primary/20"
          />
        </label>
        <div className="self-end">
          <button
            type="button"
            onClick={loadRecords}
            className="inline-flex items-center gap-2 rounded-xl bg-primary px-5 py-2.5 text-sm font-bold text-on-primary shadow-sm transition-colors hover:bg-primary/90"
          >
            <span className="material-symbols-outlined text-[18px]">search</span>
            Search
          </button>
        </div>
      </div>

      <div className="flex items-center justify-between rounded-2xl bg-primary px-6 py-4">
        <div className="flex items-center gap-3">
          <span className="material-symbols-outlined text-on-primary text-[22px]">fingerprint</span>
          <h2 className="text-lg font-bold text-on-primary">Biometric Records</h2>
        </div>
        <span className="inline-flex items-center rounded-full bg-on-primary/20 px-3 py-1 text-xs font-bold text-on-primary">
          {entryCount} {entryCount === 1 ? 'entry' : 'entries'}
        </span>
      </div>

      <div className="overflow-hidden rounded-2xl border border-outline-variant/30 bg-surface-container-lowest">
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="border-b border-outline-variant/30 bg-surface-container-low">
              <th className="px-5 py-3.5 text-xs font-black uppercase tracking-wider text-on-surface-variant">Time</th>
              <th className="px-5 py-3.5 text-xs font-black uppercase tracking-wider text-on-surface-variant">ID Card No</th>
              <th className="px-5 py-3.5 text-xs font-black uppercase tracking-wider text-on-surface-variant">Student's Name</th>
              <th className="px-5 py-3.5 text-xs font-black uppercase tracking-wider text-on-surface-variant">Branch</th>
              <th className="px-5 py-3.5 text-xs font-black uppercase tracking-wider text-on-surface-variant">Company</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={5} className="px-5 py-16 text-center">
                  <span className="material-symbols-outlined animate-spin text-3xl text-primary/40">progress_activity</span>
                  <p className="mt-3 text-sm text-on-surface-variant">Loading biometric records...</p>
                </td>
              </tr>
            ) : records.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-5 py-12">
                  <div className="flex items-center justify-center rounded-xl border-2 border-dashed border-blue-200 bg-blue-50/50 px-6 py-8">
                    <p className="text-center text-sm font-bold leading-relaxed text-blue-600">
                      No entries meet selected criteria — Either there are no entries in the selected
                      module or you have selected invalid filter conditions.
                    </p>
                  </div>
                </td>
              </tr>
            ) : (
              records.map((record, idx) => (
                <tr
                  key={idx}
                  className={`border-b border-outline-variant/10 ${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'} hover:bg-primary/5 transition-colors`}
                >
                  <td className="px-5 py-3.5 text-sm text-on-surface font-mono">{record.time}</td>
                  <td className="px-5 py-3.5 text-sm text-on-surface font-mono">{record.idCardNo}</td>
                  <td className="px-5 py-3.5 text-sm font-semibold text-on-surface">{record.studentName}</td>
                  <td className="px-5 py-3.5 text-sm text-on-surface-variant">{record.branch}</td>
                  <td className="px-5 py-3.5 text-sm text-on-surface-variant">{record.company}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <section className="mt-2 rounded-2xl border border-outline-variant/20 bg-surface-container-low px-8 py-5">
        <div className="flex flex-col gap-1 text-xs text-on-surface-variant">
          {student?.createdBy && (
            <span>
              Created by <span className="font-bold text-on-surface">{student.createdBy}</span> on{' '}
              <span className="font-semibold text-on-surface">{student.createdDate}</span>
            </span>
          )}
          {student?.updatedBy && (
            <span>
              Updated by <span className="font-bold text-on-surface">{student.updatedBy}</span> on{' '}
              <span className="font-semibold text-on-surface">{student.updatedDate}</span>
            </span>
          )}
        </div>
      </section>
    </div>
  )
}
